import { getFullAddress, safeValue } from "../pages/repo/vehicles/findVehiclesHelpers";
import { repoCaseService } from "../services/repoCase.service";
import { getBankerFieldsFromCase, mergeExcelContactsIntoCase } from "./bankerExcelFields";
import { appendAdminFinancerMessageBody, buildAdminFinancerMessageRows } from "./adminFinancerNotifyFormat";
import { formatVehicleNumberDisplay } from "./vehicleNumberUtils";
import { resolveVehicleDisplayStatus } from "./vehicleDisplayStatus";

const ROLE_LABELS = {
  REPO_ADMIN: "Repo Admin",
  TEAM_LEADER: "Team Leader",
  HEAD_OFFICE_STAFF: "Head Office Staff",
  OFFICE_STAFF: "Office Staff",
  REPO_STAFF: "Repo Staff",
  REPO_VIEWER: "Repo Viewer",
};

function formatRole(role) {
  if (!role) return "";
  return ROLE_LABELS[role] || String(role).replace(/_/g, " ");
}

const CASE_FIELD_ROWS = [
  { label: "Registration No", get: (c) => safeValue(c.vehicleNumber) },
  { label: "Customer Name", get: (c) => safeValue(c.customerName) },
  { label: "Father Name", get: (c) => safeValue(c.fatherName) },
  { label: "Mobile No", get: (c) => safeValue(c.mobileNumber) },
  { label: "Alternate Mobile No", get: (c) => safeValue(c.alternateMobileNumber) },
  { label: "Loan Account No", get: (c) => safeValue(c.loanAccountNumber) },
  { label: "Reference No", get: (c) => safeValue(c.referenceNumber) },
  { label: "Engine No", get: (c) => safeValue(c.engineNumber) },
  { label: "Chassis No", get: (c) => safeValue(c.chassisNumber) },
  { label: "Vehicle Brand", get: (c) => safeValue(c.vehicleBrand) },
  { label: "Vehicle Model", get: (c) => safeValue(c.vehicleModel) },
  { label: "Vehicle Type", get: (c) => safeValue(c.vehicleType) },
  { label: "Registration State", get: (c) => safeValue(c.registrationState) },
  { label: "Bank / NBFC Name", get: (c) => safeValue(c.bankName) },
  { label: "Branch Name", get: (c) => safeValue(c.branchName) },
  { label: "Full Address", get: (c) => getFullAddress(c) },
  { label: "City", get: (c) => safeValue(c.city) },
  { label: "District", get: (c) => safeValue(c.district) },
  { label: "State", get: (c) => safeValue(c.state) },
  { label: "Pincode", get: (c) => safeValue(c.pincode) },
  { label: "EMI Amount", get: (c) => safeValue(c.emiAmount) },
  { label: "Due Amount", get: (c) => safeValue(c.dueAmount) },
  { label: "Outstanding Amount", get: (c) => safeValue(c.totalOutstandingAmount) },
  { label: "Bucket", get: (c) => safeValue(c.bucket) },
  { label: "Priority", get: (c) => safeValue(c.priority) },
  { label: "Field Notes", get: (c) => safeValue(c.fieldNotes) },
  {
    label: "Loaded details",
    get: (c) => safeValue(c.loadedDetail || c.loadedShort),
  },
  { label: "Status", get: (c) => resolveVehicleDisplayStatus(c) },
];

/** Omitted from WhatsApp / SMS / email and other shared messages. */
const MESSAGE_EXCLUDED_LABELS = new Set([
  "Case Code",
  "Loan Account No",
  "Loan Account Number",
  "Bank Name",
  "Bank / NBFC Name",
  "Branch Name",
  "Priority",
  "Repo Status",
  "Confirmation Status",
  "Status",
]);

const BANK_NOTIFY_EXCLUDED_LABELS = MESSAGE_EXCLUDED_LABELS;

const BANK_NOTIFY_FIELD_ROWS = CASE_FIELD_ROWS.filter(
  (row) => !MESSAGE_EXCLUDED_LABELS.has(row.label)
);

/** Extra fields omitted only from staff "Report to Admin" messages. */
const TRACE_REPORT_EXCLUDED_LABELS = new Set([
  "Full Address",
  "EMI Amount",
  "Due Amount",
  "Outstanding Amount",
  "Bucket",
]);

const MESSAGE_FIELD_ROWS = CASE_FIELD_ROWS.filter(
  (row) =>
    !MESSAGE_EXCLUDED_LABELS.has(row.label) &&
    !TRACE_REPORT_EXCLUDED_LABELS.has(row.label)
);

export function adminContactFromUser(authUser) {
  if (!authUser) return null;
  return {
    name: String(authUser.name || "").trim(),
    phone: String(authUser.phone || "").trim(),
    email: String(authUser.email || "").trim(),
  };
}

/** Extra fields for POST /notify-bank-traced so server message includes admin contact. */
export function buildNotifyBankApiPayload(channel, authUser, extras = {}) {
  const admin = adminContactFromUser(authUser);
  return {
    channel,
    ...extras,
    ...(admin
      ? { adminName: admin.name, adminPhone: admin.phone }
      : {}),
  };
}

function appendBankNotifyIntro(lines, context = {}) {
  const agencyName = safeValue(context.agency?.name);
  lines.push("Respected Sir,");
  lines.push("");
  if (agencyName) {
    lines.push(`Greetings from ${agencyName}.`);
    lines.push("");
  }
  lines.push(
    "This is to inform you that the below-mentioned vehicle has been successfully traced by our ground team."
  );
  lines.push("");
}

function appendBankNotifyFooter(lines, context = {}) {
  const agencyName = safeValue(context.agency?.name);
  const contactName = safeValue(context.admin?.name);
  const contactPhone = safeValue(context.admin?.phone);

  lines.push("");
  lines.push(
    "We request you to kindly confirm the status of the above vehicle on an urgent basis and advise whether the vehicle is to be REPOSSESSED (REPO) or RELEASED"
  );
  lines.push("");
  lines.push("Thank you & regards,");
  lines.push("");

  if (agencyName) {
    lines.push(agencyName);
  }
  if (contactName) {
    lines.push(`Contact person: ${contactName}`);
  }
  if (contactPhone) {
    lines.push(`Mobile: ${contactPhone}`);
  }
}

export function buildNotifyContextFromCase(caseData, authUser, company = null) {
  const traced = caseData?.latestTraceReport;
  const companyName =
    company?.companyName || authUser?.company?.companyName || "";
  return {
    tracedBy: traced
      ? {
          name: traced.requestedByName,
          role: traced.requestedByRole,
          roleLabel: formatRole(traced.requestedByRole),
          phone: traced.requestedByPhone,
          note: traced.requestNote,
          at: traced.reportedAt,
        }
      : null,
    admin: adminContactFromUser(authUser),
    agency: companyName ? { name: companyName } : null,
    includeAdminBankerRef: true,
  };
}

export function buildNotifyContextFromConfirmation(caseData, confirmation, authUser, company = null) {
  const companyName =
    company?.companyName || authUser?.company?.companyName || "";
  return {
    tracedBy: confirmation
      ? {
          name: confirmation.requestedByName,
          role: confirmation.requestedByRole,
          roleLabel:
            confirmation.requestedByRoleLabel || formatRole(confirmation.requestedByRole),
          phone: confirmation.requestedByPhone,
          note: confirmation.requestNote,
          at: confirmation.createdAt,
        }
      : null,
    admin: adminContactFromUser(authUser),
    agency: companyName ? { name: companyName } : null,
    includeAdminBankerRef: true,
    confirmation: confirmation || null,
  };
}

function formatShareLabel(label) {
  return String(label || "")
    .replace(/\bNumbers\b/g, "No")
    .replace(/\bNumber\b/g, "No");
}

export function getTraceReportToAdminMessage(item, reporter = {}, extra = {}) {
  const lines = ["Vehicle Traced — Report to Admin", ""];
  MESSAGE_FIELD_ROWS.forEach(({ label, get }) => {
    const value = get(item);
    if (value && value !== "-") lines.push(`${formatShareLabel(label)}: ${value}`);
  });
  if (reporter.name) {
    lines.push("");
    lines.push("--- Traced by (field / team report) ---");
    lines.push(`Name: ${reporter.name}`);
    if (reporter.roleLabel || reporter.role) {
      lines.push(`Role: ${reporter.roleLabel || formatRole(reporter.role)}`);
    }
    if (reporter.phone) lines.push(`Mobile: ${reporter.phone}`);
    if (extra.requestNote) lines.push(`Field note: ${extra.requestNote}`);
    const reportedAt = extra.reportedAt ? new Date(extra.reportedAt) : new Date();
    if (!Number.isNaN(reportedAt.getTime())) {
      lines.push(`Reported at: ${reportedAt.toLocaleString()}`);
    }
  }
  return lines.join("\n");
}

export function messageMatchesVehicle(text, vehicleNumber) {
  const plate = String(vehicleNumber || "")
    .replace(/[\s\-_.]/g, "")
    .toUpperCase();
  const haystack = String(text || "")
    .replace(/[\s\-_.]/g, "")
    .toUpperCase();
  return Boolean(plate) && haystack.includes(plate);
}

function isRepoIntimationNotify(context = {}) {
  const confirmation = context.confirmation || {};
  return Boolean(
    confirmation._id ||
      confirmation.id ||
      confirmation.status ||
      confirmation.inventoryConfirmed ||
      confirmation.inventoryConfirmedAt
  );
}

function financerValueMap(caseDoc = {}) {
  const map = {};
  buildAdminFinancerMessageRows(caseDoc).forEach(({ label, value }) => {
    const text = safeValue(value);
    map[String(label || "").toLowerCase()] = text === "—" ? "" : text;
  });
  return map;
}

function parkingYardAddress(confirmation = {}) {
  return [
    confirmation.parkingYardAddress,
    confirmation.parkingYardArea,
    confirmation.parkingYardPincode,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

function yardContactPerson(confirmation = {}) {
  const name = safeValue(confirmation.parkingYardManagerName);
  const phone = safeValue(confirmation.parkingYardManagerPhone);
  if (name && phone) return `${name} (${phone})`;
  return name || phone;
}

function resolveNotifyLoan(caseDoc = {}, fields = {}) {
  return (
    safeValue(fields["loan no"]) ||
    safeValue(caseDoc.loanAccountNumber) ||
    safeValue(caseDoc.loanNumber)
  );
}

function buildInventoryConfirmedBankNotifyText(caseDoc = {}, context = {}) {
  const confirmation = context.confirmation || {};
  const fields = financerValueMap(caseDoc);
  const plateRaw = formatVehicleNumberDisplay(
    caseDoc.vehicleNumber || confirmation.vehicleNumber
  );
  const registration =
    (plateRaw && plateRaw !== "—" ? plateRaw : "") || safeValue(fields["registration no"]);
  const loan = resolveNotifyLoan(caseDoc, fields);
  const loaded = safeValue(caseDoc.loadedDetail || caseDoc.loadedShort);
  const agencyName = safeValue(context.agency?.name);
  const contactName = safeValue(context.admin?.name);
  const contactPhone = safeValue(context.admin?.phone);

  const lines = [
    "Dear Sir",
    "Please update the repossession case below",
    "",
    "",
    `Registration No: ${registration}`,
    `Customer Name: ${safeValue(fields["customer name"]) || safeValue(caseDoc.customerName)}`,
    `Loan No: ${loan}`,
    `Make: ${safeValue(fields.make) || safeValue(caseDoc.vehicleBrand)}`,
    `Engine No: ${safeValue(fields["engine no"]) || safeValue(caseDoc.engineNumber)}`,
    `Chassis No: ${safeValue(fields["chassis no"]) || safeValue(caseDoc.chassisNumber)}`,
    `Model: ${safeValue(fields.model) || safeValue(caseDoc.vehicleModel)}`,
    `Loaded details : ${loaded}`,
    "",
    `This vehicle is parked at : ${safeValue(confirmation.parkingYardName)}`,
    `Parking yard address : ${parkingYardAddress(confirmation)}`,
    `Yard contact person : ${yardContactPerson(confirmation)}`,
    `Yard code : ${safeValue(confirmation.parkingYardCode)}`,
    `Bank / NBFC Name: ${safeValue(fields["bank name"]) || safeValue(caseDoc.bankName) || safeValue(confirmation.bankName)}`,
    `Branch Name: ${safeValue(fields["branch name"]) || safeValue(caseDoc.branchName) || safeValue(confirmation.branchName)}`,
    "",
    "Thanks & regards,",
    "",
  ];
  if (agencyName) lines.push(agencyName);
  if (contactName) lines.push(`Contact person: ${contactName}`);
  if (contactPhone) lines.push(`Mobile: ${contactPhone}`);
  return lines.join("\n");
}

export function getBankTracedShareSubject(item, context = {}) {
  const confirmation = context.confirmation || {};
  const fields = financerValueMap(item);
  const plateRaw = formatVehicleNumberDisplay(
    item?.vehicleNumber || confirmation.vehicleNumber
  );
  const registration = plateRaw && plateRaw !== "—" ? plateRaw : "—";
  const loan = resolveNotifyLoan(item, fields) || "—";
  if (confirmation.inventoryConfirmed || confirmation.inventoryConfirmedAt) {
    return `Repo Intimation ${registration} / ${loan}`;
  }
  return `Trace vehicle ${registration} Loan number ${loan}`;
}

/** Bank / external notify — financer Excel layout for admin (WhatsApp / SMS / email). */
export function getBankTracedShareMessage(item, context = {}) {
  if (isRepoIntimationNotify(context)) {
    return buildInventoryConfirmedBankNotifyText(item, context);
  }
  const lines = [];
  appendBankNotifyIntro(lines, context);

  if (context.includeAdminBankerRef === false) {
    BANK_NOTIFY_FIELD_ROWS.forEach(({ label, get }) => {
      const value = get(item);
      if (value && value !== "-") {
        lines.push(`${formatShareLabel(label)}: ${value}`);
      }
    });
  } else {
    appendAdminFinancerMessageBody(lines, item);
  }

  appendBankNotifyFooter(lines, context);
  return lines.join("\n");
}

function normalizePhoneDigits(phone) {
  return String(phone || "").replace(/\D/g, "");
}

export async function copyBankNotifyMessage(message) {
  try {
    await navigator.clipboard.writeText(message);
    return true;
  } catch {
    return false;
  }
}

export function openWhatsAppUrl(url, preopenedWindow = null) {
  if (!url) return false;
  if (preopenedWindow && !preopenedWindow.closed) {
    try {
      preopenedWindow.location.href = url;
      preopenedWindow.focus();
      return true;
    } catch {
      /* fall through */
    }
  }
  const windowName = `wa-share-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const opened = window.open(url, windowName, "noopener,noreferrer");
  if (opened) return true;
  const a = document.createElement("a");
  a.href = url;
  a.target = windowName;
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return true;
}

export function buildClientWhatsAppUrl(phone, text) {
  const digits = normalizePhoneDigits(phone);
  const waPhone =
    digits.length === 10 ? `91${digits}` : digits.length > 10 ? digits : "";
  const base = waPhone
    ? `https://wa.me/${waPhone}?text=`
    : "https://wa.me/?text=";
  let body = String(text || "");
  let encoded = encodeURIComponent(body);
  if (base.length + encoded.length > 1800) {
    body = `${body.slice(0, 400)}\n\n(Full details copied to clipboard.)`;
    encoded = encodeURIComponent(body);
  }
  return `${base}${encoded}`;
}

export function listBankerNotifyRecipients(caseData = {}, apiOptions = []) {
  const seen = new Map();
  const out = [];
  const bankers = getBankerFieldsFromCase(caseData) || {};

  const add = (id, label, name, phone, email, { uniqueByPhone = true } = {}) => {
    const digits = normalizePhoneDigits(phone);
    const mail = String(email || "").trim();
    if (digits.length < 10 && !mail.includes("@")) return;
    const last10 = digits.length >= 10 ? digits.slice(-10) : "";
    const cleanName = String(name || "").trim();
    const key = uniqueByPhone
      ? last10 || mail.toLowerCase()
      : `${id}:${last10 || mail.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.set(key, out.length);
    out.push({
      id: String(id || key),
      label: String(cleanName || label || "Banker").trim(),
      name: cleanName,
      phone: last10,
      email: mail,
    });
  };

  add(
    "cp1",
    "1st banker",
    bankers.banker1Name,
    bankers.banker1Phone || caseData.contactPerson1Phone,
    caseData.bankNotifyEmail1,
    { uniqueByPhone: false }
  );
  add(
    "cp2",
    "2nd banker",
    bankers.banker2Name,
    bankers.banker2Phone || caseData.contactPerson2Phone,
    caseData.bankNotifyEmail2,
    { uniqueByPhone: false }
  );
  add(
    "cp3",
    "3rd banker",
    bankers.banker3Name,
    bankers.banker3Phone || caseData.contactPerson3Phone,
    caseData.bankNotifyEmail3,
    { uniqueByPhone: false }
  );

  if (out.length) return out;

  for (const option of Array.isArray(apiOptions) ? apiOptions : []) {
    if (!option || option.id === "custom" || option.id === "branch") continue;
    add(option.id, option.label, option.name, option.phone, option.email);
  }
  return out;
}

export function getAdminFromEmail(user = {}) {
  const candidates = [
    user?.email,
    user?.company?.email,
    user?.company?.adminEmail,
    user?.companyEmail,
  ];
  for (const value of candidates) {
    const email = String(value || "").trim();
    if (email.includes("@")) return email;
  }
  return "";
}

export function buildGmailComposeHref({ body = "", from = "", subject = "" } = {}) {
  let text = String(body || "");
  const fromEmail = String(from || "").trim();
  const sub = String(subject || "").trim();
  const parts = ["view=cm", "fs=1"];
  if (fromEmail.includes("@")) {
    parts.push(`authuser=${encodeURIComponent(fromEmail)}`);
  }
  if (sub) {
    parts.push(`su=${encodeURIComponent(sub)}`);
  }
  const prefix = `https://mail.google.com/mail/?${parts.join("&")}&body=`;
  let href = `${prefix}${encodeURIComponent(text)}`;
  if (href.length > 1800) {
    copyBankNotifyMessage(text);
    text = `${text.slice(0, 500)}\n\n(Full message copied to clipboard.)`;
    href = `${prefix}${encodeURIComponent(text)}`;
  }
  return href;
}

/** Opens Gmail compose from the admin account. To stays empty for manual entry. */
export function openGmailCompose({ body = "", from = "", subject = "" } = {}) {
  const href = buildGmailComposeHref({ body, from, subject });
  const a = document.createElement("a");
  a.href = href;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return href;
}

export function buildMailtoHref({ to = "", subject = "", body = "" } = {}) {
  const address = String(to || "").trim().replace(/[<>"]/g, "");
  const sub = String(subject || "");
  let text = String(body || "");
  const prefix = `mailto:${address}?subject=${encodeURIComponent(sub)}&body=`;
  let href = `${prefix}${encodeURIComponent(text)}`;
  if (href.length > 1800) {
    copyBankNotifyMessage(text);
    text = `${text.slice(0, 500)}\n\n(Full message copied to clipboard.)`;
    href = `${prefix}${encodeURIComponent(text)}`;
  }
  return href;
}

/** Opens the OS mail app on this page. Never use a new about:blank tab — Chrome leaves that stuck. */
export function openMailtoComposer({ to = "", subject = "", body = "" } = {}) {
  const href = buildMailtoHref({ to, subject, body });
  const a = document.createElement("a");
  a.href = href;
  a.target = "_self";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.location.href = href;
  return href;
}

export function buildSmsHref({ to = "", body = "" } = {}) {
  const digits = String(to || "").replace(/\D/g, "");
  const phone =
    digits.length === 10 ? `+91${digits}` : digits ? `+${digits}` : "";
  let text = String(body || "");
  const prefix = phone ? `sms:${phone}?body=` : "sms:?body=";
  let href = `${prefix}${encodeURIComponent(text)}`;
  if (href.length > 1800) {
    copyBankNotifyMessage(text);
    text = `${text.slice(0, 400)}\n\n(Full message copied to clipboard.)`;
    href = `${prefix}${encodeURIComponent(text)}`;
  }
  return href;
}

export function openPhoneCall(phone) {
  const digits = normalizePhoneDigits(phone);
  if (!digits) return "";
  const href =
    digits.length === 10
      ? `tel:+91${digits}`
      : digits.startsWith("91") && digits.length === 12
        ? `tel:+${digits}`
        : `tel:+${digits}`;
  window.location.href = href;
  return href;
}

export function openSmsComposer({ to = "", body = "" } = {}) {
  const href = buildSmsHref({ to, body });
  const a = document.createElement("a");
  a.href = href;
  a.target = "_self";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.location.href = href;
  return href;
}

/** Staff trace report — opens WhatsApp / email / SMS with full vehicle + tracer details. */
export function openTraceReportShare(type, item, reporter = {}, extra = {}) {
  const message = extra.message || getTraceReportToAdminMessage(item, reporter, extra);
  const registration = String(item?.vehicleNumber || "").trim() || "—";
  const subject = extra.subject || `Trace Vehicle ${registration}`;
  const adminEmail = String(extra.adminEmail || "").trim();
  const adminPhone = String(extra.adminPhone || "").trim();

  if (type === "whatsapp") {
    copyBankNotifyMessage(message);
    const digits = normalizePhoneDigits(adminPhone);
    const waPhone = digits.length === 10 ? `91${digits}` : digits;
    const backendUrl = String(extra.whatsAppUrl || "");
    const url =
      backendUrl && messageMatchesVehicle(backendUrl, registration)
        ? backendUrl
        : waPhone
          ? `https://api.whatsapp.com/send?phone=${waPhone}&text=${encodeURIComponent(message)}`
          : `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    openWhatsAppUrl(url);
    return { copied: true };
  }

  if (type === "email") {
    openGmailCompose({
      body: message,
      from: extra.fromEmail || reporter?.email || "",
      subject,
    });
    copyBankNotifyMessage(message).catch(() => {});
    return { to: "" };
  }

  if (type === "sms") {
    const digits = normalizePhoneDigits(adminPhone);
    const href = digits
      ? `sms:+${digits.length === 10 ? `91${digits}` : digits}?body=${encodeURIComponent(message)}`
      : `sms:?body=${encodeURIComponent(message)}`;
    window.location.href = href;
    return {};
  }

  return {};
}

async function resolveBankNotifyMessage(item, context = {}, token, contacts = {}, options = {}) {
  if (token) {
    try {
      const res = await repoCaseService.fetchBankNotifyMessage(token, {
        caseId: options.caseId || item?._id || item?.id,
        searchItem: item,
        adminName: context?.admin?.name,
        adminPhone: context?.admin?.phone,
        notifyPhone: contacts.notifyPhone,
        notifyEmail: contacts.notifyEmail,
      });
      if (res?.data?.message) {
        return {
          message: res.data.message,
          subject: res.data.subject || "",
          enrichedItem: mergeExcelContactsIntoCase(
            res.data.enrichedCase || item,
            res.data.excelContacts
          ),
          notifyPhone:
            String(contacts.notifyPhone || "").trim() ||
            String(res.data.bankNotifyPhone || "").trim(),
          notifyEmail:
            String(contacts.notifyEmail || "").trim() ||
            String(res.data.bankNotifyEmail || "").trim(),
        };
      }
    } catch {
      /* use local template */
    }
  }
  return {
    message: getBankTracedShareMessage(item, context),
    subject: getBankTracedShareSubject(item, context),
    notifyPhone: String(contacts.notifyPhone || "").trim(),
    notifyEmail: String(contacts.notifyEmail || "").trim(),
  };
}

function firstNotifyEmail(item, contacts = {}) {
  const candidates = [
    contacts.notifyEmail,
    item?.bankNotifyEmail1,
    item?.bankNotifyEmail2,
    item?.email,
  ];
  for (const value of candidates) {
    const s = String(value || "").trim();
    if (s.includes("@")) return s;
  }
  return "";
}

export async function openBankNotifyShare(
  type,
  item,
  contacts = {},
  context = {},
  options = {}
) {
  const { token, caseId } = options;
  const localMessage = getBankTracedShareMessage(item, context);
  const localEmail = firstNotifyEmail(item, contacts);
  const localPhone = String(contacts.notifyPhone || item?.mobileNumber || "").trim();

  if (type === "email") {
    openGmailCompose({
      body: localMessage,
      from: options.fromEmail || context?.admin?.email || "",
      subject: getBankTracedShareSubject(item, context),
    });
    copyBankNotifyMessage(localMessage).catch(() => {});
  }

  const resolved = await resolveBankNotifyMessage(item, context, token, contacts, {
    caseId,
  });
  const { message } = resolved;
  const phone = resolved.notifyPhone || localPhone;
  const email = resolved.notifyEmail || localEmail;

  if (type === "whatsapp") {
    copyBankNotifyMessage(message);
    const digits = normalizePhoneDigits(phone);
    const waPhone = digits.length === 10 ? `91${digits}` : digits;
    const url = waPhone
      ? `https://wa.me/${waPhone}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    openWhatsAppUrl(url);
    return { copied: true };
  }

  if (type === "email") {
    return { to: email };
  }

  if (type === "sms") {
    const digits = normalizePhoneDigits(phone);
    const href = digits
      ? `sms:+${digits.length === 10 ? `91${digits}` : digits}?body=${encodeURIComponent(message)}`
      : `sms:?body=${encodeURIComponent(message)}`;
    window.location.href = href;
    return {};
  }

  return {};
}

export function formatNotifyApiResult(results) {
  if (!results) return "Sent.";
  const parts = [];
  if (results.email?.ok) parts.push("Email sent");
  else if (results.email?.skipped) parts.push(`Email: ${results.email.reason || "skipped"}`);
  else if (results.email) parts.push(`Email: ${results.email.error || "failed"}`);
  if (results.sms?.ok) parts.push("SMS sent");
  else if (results.sms?.skipped) parts.push(`SMS: ${results.sms.reason || "skipped"}`);
  else if (results.sms) parts.push(`SMS: ${results.sms.error || "failed"}`);
  if (results.whatsapp?.ok) parts.push("WhatsApp opened");
  return parts.join(". ") || "Done.";
}

const CHANNEL_LABEL = {
  whatsapp: "WhatsApp",
  sms: "SMS",
  email: "Email",
};

export async function sendConfirmationBankNotify({
  channel,
  caseId,
  caseData,
  confirmation,
  token,
  authUser,
  notifyPhone = "",
  notifyEmail = "",
  recipientId = "",
  skipClientOpen = false,
  previewMessage = "",
}) {
  const selectedPhone = String(notifyPhone || "").trim();
  const payload = {
    ...buildNotifyBankApiPayload(channel, authUser, {
      notifyPhone: selectedPhone,
      notifyEmail,
      recipientId,
    }),
    confirmationId: confirmation?._id || confirmation?.id,
    searchItem: caseData,
  };

  if (channel === "whatsapp") {
    const localMessage =
      previewMessage ||
      getBankTracedShareMessage(
        caseData,
        buildNotifyContextFromConfirmation(caseData, confirmation, authUser)
      );
    const localUrl = buildClientWhatsAppUrl(selectedPhone, localMessage);
    if (localUrl && !skipClientOpen) {
      openWhatsAppUrl(localUrl);
    }
    if (localMessage) {
      copyBankNotifyMessage(localMessage).catch(() => {});
    }

    try {
      await repoCaseService.notifyBankTraced(caseId, payload, token);
    } catch {
      /* WhatsApp already opened. A missing banker number still uses wa.me with the message. */
    }

    return {
      ok: true,
      status: "sent",
      message: selectedPhone
        ? `WhatsApp opened for ${selectedPhone}.`
        : "WhatsApp opened.",
    };
  }

  if (channel === "email") {
    const localMessage =
      previewMessage ||
      getBankTracedShareMessage(
        caseData,
        buildNotifyContextFromConfirmation(caseData, confirmation, authUser)
      );
    const emails = String(notifyEmail || "")
      .split(/[;,]/)
      .map((value) => value.trim())
      .filter((value) => value.includes("@"));

    if (confirmation) {
      if (!emails.length) {
        throw new Error("Enter the banker email address, then send.");
      }
      const res = await repoCaseService.notifyBankTraced(
        caseId,
        {
          ...payload,
          channel: "email",
          notifyEmail: emails.join(", "),
        },
        token
      );
      const data = res?.data || {};
      if (data.results?.email?.ok) {
        const attached = Number(data.results.email.attached || 0);
        return {
          ok: true,
          status: "sent",
          message: `Email sent${data.usedRecipients?.email ? ` to ${data.usedRecipients.email}` : ""}${
            attached ? ` with ${attached} attachment(s)` : " (no inventory files found to attach)"
          }.`,
        };
      }
      throw new Error(
        data.results?.email?.error ||
          data.results?.email?.reason ||
          res?.message ||
          "Could not send the bank / NBFC email."
      );
    }

    if (!skipClientOpen) {
      openGmailCompose({
        body: localMessage,
        from: getAdminFromEmail(authUser),
        subject: getBankTracedShareSubject(
          caseData,
          buildNotifyContextFromConfirmation(caseData, confirmation, authUser)
        ),
      });
    }
    copyBankNotifyMessage(localMessage).catch(() => {});
    return {
      ok: true,
      status: "sent",
      message: "Gmail opened from your admin email. Add the To address and send.",
    };
  }

  if (channel === "sms") {
    const localMessage =
      previewMessage ||
      getBankTracedShareMessage(
        caseData,
        buildNotifyContextFromConfirmation(caseData, confirmation, authUser)
      );
    if (!skipClientOpen) {
      openSmsComposer({ to: selectedPhone, body: localMessage });
    }
    copyBankNotifyMessage(localMessage).catch(() => {});
    if (selectedPhone) {
      try {
        const res = await repoCaseService.notifyBankTraced(caseId, payload, token);
        const data = res?.data || {};
        if (data.results?.sms?.ok) {
          return {
            ok: true,
            status: "sent",
            message: `SMS sent to ${selectedPhone}.`,
          };
        }
      } catch {
        /* SMS app already opened */
      }
    }
    return {
      ok: true,
      status: "sent",
      message: selectedPhone
        ? `SMS opened for ${selectedPhone}. Complete send in your SMS app if needed.`
        : "SMS opened.",
    };
  }

  throw new Error(`${CHANNEL_LABEL[channel] || channel} is not supported.`);
}

export { CASE_FIELD_ROWS };

export { filterCaseFieldRows, shouldShowAdminOnlyCaseFields } from "./caseFieldVisibility";
